import React, { useEffect, useState, useRef } from 'react';

import { useEffectThrottled, useIsInitialRender } from 'hooks';
import Table from '../Table';
import Text from '../Text';
import Input from '../Input';
import Select from '../Select';
import Pagination from './Pagination';

export default function DataTable({
  data,
  columns,
  total,
  pages,
  currentPage,
  defaultPerPage = 5,
  searchPlaceholder,
  onSearchChanged,
  onPageChanged,
  onPerPageChanged,
  loading,
  withFilterOption = true,
  perPageOptions = [{ v: 3 }, { v: 5 }, { v: 10 }],
  withPagination = true,
  className,
  tableLayout = 'auto',
  tableStyles = {},
  perPagePosition = 'top',
  emptyMessage = 'Não há informações.',
  // Conteúdo opcional à direita da linha de cima, alinhado com o seletor de itens por página.
  HeaderRightComponent = null,
}) {
  const [perPage, setPerPage] = useState({ v: defaultPerPage });
  const [search, setSearch] = useState('');

  const paginationRef = useRef(null);
  const isInitialRender = useIsInitialRender();

  useEffect(() => {
    const parsedDefaultPerPage = Number(defaultPerPage);

    if (Number.isFinite(parsedDefaultPerPage) && parsedDefaultPerPage > 0) {
      setPerPage({ v: parsedDefaultPerPage });
    }
  }, [defaultPerPage]);

  function handlePerPageChange(value) {
    setPerPage(value);

    if (onPerPageChanged) {
      onPerPageChanged(value.v);
    }
  }

  function handleSearchChange(value) {
    setSearch(value);
  }

  useEffectThrottled(() => {
    if (!isInitialRender) {
      paginationRef.current.resetPage();

      if (onSearchChanged) {
        onSearchChanged(search);
      }
    }
  }, [search]);

  const showPerPageOnTop = perPagePosition === 'top' || perPagePosition === 'both';
  const showPerPageOnBottom = perPagePosition === 'bottom' || perPagePosition === 'both';

  const perPageSelector = (
    <div className="d-flex align-items-center">
      <Text type="label" color="dark">
        Exibir
      </Text>
      <Select
        onChange={handlePerPageChange}
        value={perPage}
        options={perPageOptions}
        getOptionLabel={(option) => option.v}
        getOptionValue={(option) => option.v}
        placeholder={null}
        disableSearch
        disableClear
        components={{
          IndicatorsContainer: () => null,
        }}
        styles={{
          containerStyle: {
            width: 'auto',
            minWidth: 40,
            marginBottom: 0,
            marginRight: 5,
            marginLeft: 5,
          },
        }}
      />
      <Text type="label" color="dark">
        por página
      </Text>
    </div>
  );

  return (
    <div className={className}>
      <div className="d-flex align-items-center justify-content-between">
        {withPagination && showPerPageOnTop && perPageSelector}

        {withFilterOption ? (
          <div className="d-flex align-items-center">
            <Text type="label" color="dark">
              Filtrar
            </Text>
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={({ target }) => handleSearchChange(target.value)}
              containerStyle={{
                marginBottom: 0,
                marginLeft: 10,
                maxWidth: 350,
              }}
            />
          </div>
        ) : null}

        {HeaderRightComponent ? (
          <div className="d-flex align-items-center" style={{ marginLeft: 'auto' }}>
            {HeaderRightComponent}
          </div>
        ) : null}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <Table tableLayout={tableLayout} tableStyles={tableStyles}>
          <thead>
            <tr>
              {columns?.map((column, index) => (
                <th key={column?.id ?? column?.name ?? column?.title ?? index}>
                  <Text color="dark" weight="500" title={column?.title}>
                    {column.name}
                  </Text>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data && data.length > 0 ? (
              data.map((row, rowIndex) => {
                return (
                  <tr key={row?.id ?? rowIndex}>
                    {columns.map((column, colIndex) => {
                      const cellKey = column?.id ?? column?.name ?? column?.title ?? colIndex;
                      if (column.selector) {
                        const value = column.selector(row);
                        return (
                          <td className={column.className} style={column.styles}>
                            <Text type="regular" weight="400" color="dark">
                              {value}
                            </Text>
                          </td>
                        );
                      }

                      return (
                        <td className={column.className} style={column.styles}>
                          {column.render(row)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns?.length || 1}>
                  <Text type="regular" color="gray">
                    {emptyMessage}
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      {withPagination && (
        <div className="d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center">
            {showPerPageOnBottom && <div className="mr-3">{perPageSelector}</div>}
            <Text type="label" color="gray">
              Exibindo {data?.length ?? 0} de {total} registros
            </Text>
          </div>
          <div>
            <Pagination
              currentPage={currentPage}
              pages={pages}
              onPageChanged={onPageChanged}
              ref={paginationRef}
            />
          </div>
        </div>
      )}
    </div>
  );
}
